// =============================================================================
// ui/shell/FilterDrawer.tsx — the single global-filter surface.
// -----------------------------------------------------------------------------
// One button, one panel: Date + Account + facets. The old client consolidated
// its separate header date and account dropdowns into exactly this in v2.65, and
// the reason is worth keeping: a global filter that lives in three places is one
// a user can leave set somewhere they are not looking, and every KPI on every
// tab is quietly scoped by it.
//
// That is also why the chip bar (FilterChips) is ALWAYS visible rather than
// living inside the drawer — a stuck filter must never be invisible.
//
// ── THE SECTION ORDER IS BETA'S, AND IT IS A PARITY CLAIM (D85) ──
//
// Date · Account · Instrument · Tag · Pattern · Day of week · Annotation. That
// is `renderFilterDrawerBody()` verbatim. A composition test asserts it, because
// a per-section test answers "does this section work", never "should this
// section be here, in this place".
//
// ⚠ THE INVENTORY COMMENT THAT USED TO LIVE HERE IS GONE, not updated. It listed
// which facets were wired, and it was wrong for one commit after Patterns
// shipped — the same rot as the journal's "what's writable" card (D68). Every
// dimension is wired now, so there is nothing left for a hand-kept list to be
// wrong about. Do not start another one.
//
// ⚠ STRUCTURE, NOT THE DESIGN SYSTEM. Same standing caveat as the rest of ui/.
// =============================================================================

import { useMemo, useState } from 'react';
import { useAccounts } from '../state/accounts';
import { useDemo } from '../state/demo';
import { usePatterns } from '../state/patterns';
import { usePreferences } from '../state/preferences';
import {
  useActiveFacets, useFilters, UNASSIGNED_FILTER_ID, type FacetDim,
} from '../state/filters';
import { useRange } from '../state/range';
import { RangeControls } from './RangePicker';
import { Icon } from './Icon';
import {
  annotationOptions, dayOfWeekOptions, instrumentOptions, parseTagRegistry,
  patternOptions, tagOptions,
  type FacetTrade,
} from '../../selectors/accounts';
import type { Account } from '../../selectors/accounts';

export interface FilterDrawerProps {
  /**
   * The facet-count UNIVERSE.
   *
   * ⚠ Account-scoped but NOT date-scoped, matching the old client — otherwise
   * options appear and disappear as the date filter moves, which is the
   * behaviour that consolidation was made to fix. Null while it loads; the
   * sections render without counts rather than blocking the drawer.
   */
  universe: readonly FacetTrade[] | null;
  /**
   * The open tab id.
   *
   * ⚠ Needed because ANALYTICS AND CALENDAR IGNORE THE GLOBAL DATE FILTER — each
   * has its own scope control. The old client DISABLES its date button on those
   * tabs with a tooltip saying why, and that is the honest behaviour: a filter
   * that looks set but is not applied is worse than one visibly switched off,
   * because every number gets read through a scope that was never used.
   */
  tabId?: string | undefined;
}

/** Tabs whose own scope control replaces the global date range. */
export function tabIgnoresGlobalDate(tabId: string | undefined): string | null {
  if (!tabId) return null;
  if (tabId === 'calendar') return 'Calendar uses its own month navigation.';
  if (tabId.startsWith('analytics')) {
    return 'Analytics uses its own period chips (1W / 1M / 3M / YTD / 1Y / All).';
  }
  return null;
}

/**
 * The section order, as one list.
 *
 * ⚠ EXPORTED so a test can assert the rendered headings against it — and
 * transcribed from beta's `renderFilterDrawerBody()`. Composition is a parity
 * claim (D85): a Tag section that works perfectly, placed after Pattern, is
 * still wrong against the screen it is a port of.
 */
export const FILTER_SECTIONS = [
  'Date', 'Account', 'Instrument', 'Tag', 'Pattern', 'Day of week', 'Annotation',
] as const;

export function FilterDrawer({ universe, tabId }: FilterDrawerProps) {
  const [open, setOpen] = useState(false);
  const { accounts } = useAccounts();
  const { patterns } = usePatterns();
  const { prefs } = usePreferences();
  const { mode, label: rangeLabel } = useRange();
  const f = useFilters();
  // ⚠ PRUNED, and centrally — see the header of `state/filters.tsx`. What the
  // drawer ticks, what the chip bar names and what actually filters the book are
  // now one value; they were three, and the drawer was the only one pruning.
  const { facets, facetCount, anyActive } = useActiveFacets();

  // ⚠ Parsed with the SAME function the trade form uses. Two parsers would
  // disagree about which tags exist — you could apply one on a trade and not
  // find it in the filter list, with neither screen looking wrong.
  const registry = useMemo(() => parseTagRegistry(prefs?.customTags), [prefs]);

  const accountLabel = useMemo(() => {
    if (!f.accounts.length) return 'All accounts';
    if (f.accounts.length === 1) {
      const id = f.accounts[0] as string;
      if (id === UNASSIGNED_FILTER_ID) return 'Unassigned';
      return (accounts ?? []).find((a) => String(a.id) === id)?.name || '1 account';
    }
    return `${f.accounts.length} accounts`;
  }, [f.accounts, accounts]);

  const dateIgnored = tabIgnoresGlobalDate(tabId);

  return (
    <>
      <button
        type="button"
        className={`btn filter-btn${anyActive ? ' is-active' : ''}`}
        // ⚠ The visible text is now the SCOPE, so the accessible name has to say
        // what the control IS. Without this a screen reader announces
        // "This week · All accounts, button" and never the word filter.
        aria-label="Filters — date, account, instrument, tag, pattern, day"
        title="Filter trades — date, account, instrument, tag, pattern, day"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
      >
        {/* ⚠ A FUNNEL AND THE SCOPE — NO WORD "FILTERS" (#51). Beta's button is
            an icon plus the filter STATE ("All trades"), and the word was pure
            duplication: an icon that needs a label beside it saying what kind of
            control it is has not earned its place, and the scope text is what
            the user actually reads. The accessible name is on the button. */}
        <Icon name="filter" />
        <span className="muted filter-btn-scope">
          {' '}{dateIgnored ? 'own period' : rangeLabel} · {accountLabel}
        </span>
        {facetCount ? <span className="filter-badge">{facetCount}</span> : null}
      </button>

      {open ? (
        <>
          <div className="drawer-scrim" onClick={() => setOpen(false)} aria-hidden="true" />
          <aside className="filter-drawer" role="dialog" aria-modal="true" aria-label="Filters">
            <header className="filter-drawer-head">
              <b>Filters</b>
              <button type="button" className="btn" onClick={() => setOpen(false)}>Close</button>
            </header>

            <div className="filter-drawer-body">
              <Section title="Date" value={dateIgnored ? 'not used here' : rangeLabel}>
                {dateIgnored ? (
                  <p className="muted">
                    {dateIgnored} The global date filter is disabled on this tab —
                    it is still set to <b>{rangeLabel}</b> for every other tab.
                  </p>
                ) : <RangeControls />}
              </Section>

              <Section title="Account" value={accountLabel}>
                <AccountSection accounts={accounts} />
              </Section>

              <Section title="Instrument" value={count(facets.instruments)}>
                <FacetSection
                  dim="instruments"
                  options={universe ? instrumentOptions(universe) : []}
                  active={facets.instruments}
                  empty="No instruments yet."
                />
              </Section>

              {/* ⚠ The REGISTRY drives this list, like Pattern and unlike
                  Instrument: a tag the user created but has not applied yet
                  still appears, at count 0. One vanishing because it is unused
                  reads as a tag that was deleted. */}
              <Section title="Tag" value={count(facets.tags)}>
                <FacetSection
                  dim="tags"
                  options={universe ? tagOptions(universe, registry) : []}
                  active={facets.tags}
                  empty={prefs
                    ? 'No custom tags yet — create them on a trade or in Settings.'
                    : 'Loading your tags…'}
                />
              </Section>

              {/* ⚠ The catalogue drives this list, NOT the universe — the
                  opposite of Instrument. A pattern with zero trades still
                  appears at count 0, because the list is the user's own named
                  setups and one vanishing reads as a deleted pattern. */}
              <Section title="Pattern" value={count(facets.patterns)}>
                <FacetSection
                  dim="patterns"
                  options={universe && patterns ? patternOptions(universe, patterns) : []}
                  active={facets.patterns}
                  empty={patterns && patterns.length === 0
                    ? 'No patterns yet — create them on the Patterns tab.'
                    : 'Loading your patterns…'}
                />
              </Section>

              <Section title="Day of week" value={count(facets.daysOfWeek)}>
                <FacetSection
                  dim="daysOfWeek"
                  options={universe && mode ? dayOfWeekOptions(universe, mode) : []}
                  active={facets.daysOfWeek}
                  empty="No trades yet."
                />
              </Section>

              {/* ⚠ EVERY option is TRUE when the trade LACKS the piece — this is
                  the queue of what still needs reviewing, so "Missing grade"
                  means "no grade". All five always render, including at zero: a
                  row that disappears when the work is done looks like a broken
                  filter rather than an emptied queue.

                  ⚠ Deliberately flag-agnostic. None of them consults
                  `annotated`, because that bare boolean can be set on a trade
                  that is still empty — bypassing it is the entire point. */}
              <Section title="Annotation" value={count(facets.annotation)}>
                <FacetSection
                  dim="annotation"
                  options={universe ? annotationOptions(universe) : []}
                  active={facets.annotation}
                  empty="No trades yet."
                />
              </Section>
            </div>

            <footer className="filter-drawer-foot">
              {/* ⚠ Clear appears only when there IS something to clear, as in the
                  old client — a permanently enabled Clear implies a filter is on.
                  It leaves the DATE range alone: date has its own reset inside its
                  own section, and wiping it from here would silently widen every
                  number on the screen. */}
              {anyActive ? (
                <button type="button" className="btn" onClick={f.clearAll}>Clear filters</button>
              ) : null}
              <button type="button" className="btn primary" onClick={() => setOpen(false)}>Done</button>
            </footer>
          </aside>
        </>
      ) : null}
    </>
  );
}

/**
 * A section's header count — blank rather than "0", matching the old client's
 * `facetSum`. A "0" beside a title reads as a result, not as "nothing selected".
 */
const count = (a: readonly unknown[]): string | undefined =>
  (a.length ? String(a.length) : undefined);

/**
 * ⚠ The header states the CURRENT VALUE on the right, as the old client does.
 * A section titled only "Date" makes you open it to find out what is applied —
 * on a panel whose whole job is telling you what is applied.
 */
function Section(
  { title, value, children }:
  // ⚠ `| undefined` explicitly — `exactOptionalPropertyTypes` is on, so a caller
  // passing a computed `string | undefined` is a type error without it.
  { title: string; value?: string | undefined; children: React.ReactNode },
) {
  return (
    <section className="filter-section">
      <h4 className="filter-section-title">
        <span>{title}</span>
        {value ? <span className="filter-section-value">{value}</span> : null}
      </h4>
      {children}
    </section>
  );
}

/**
 * ⚠ "All accounts" is the EMPTY selection, and it is rendered as a real, checked
 * option rather than as the absence of one. A user has to be able to get back to
 * it in one click; leaving it implicit means unticking the last account looks
 * like it should show nothing, which is the inverted reading of empty.
 *
 * ⚠ ARCHIVED AND EXCLUDED ACCOUNTS COLLAPSE INTO A SUBSECTION, matching the old
 * client. A side-by-side comparison is what forced this: the first version listed
 * all eleven flat, so a trader with a long history of blown evaluations opens the
 * panel to a wall of dead accounts and has to read every one to find the live it.
 * Beta shows the handful that are active and folds the rest away behind a count.
 *
 * The subsection still says how many of the hidden ones are SELECTED — a filter
 * you cannot see is the thing this whole panel exists to prevent.
 *
 * ── 🚨 THE DEMO LIVES HERE TOO, AND IT HAS TO ──
 *
 * The old client puts its 🎓 Demo Account beside the real ones in exactly this
 * list (app/index.html:12596), and the first port of the demo missed the point
 * by shipping only the Get Started entry — which renders ONLY when the trader has
 * NO accounts. Anyone with a book, which is every existing user and Josh, could
 * not reach the demo at all. Found the moment he asked how to test it.
 *
 * ⚠ IT IS NOT A FILTER, and it is separated from the checkboxes above for that
 * reason. Those narrow the book on screen; this REPLACES it. Rendering it as one
 * more checkbox in the same list would say the two do the same kind of thing.
 */
function AccountSection({ accounts }: { accounts: readonly Account[] | null }) {
  const f = useFilters();
  const demo = useDemo();
  const [showHidden, setShowHidden] = useState(false);

  if (!accounts) return <p className="muted">Loading accounts…</p>;

  const all = f.accounts.length === 0;
  const deEmph = (a: Account) => a.archived || a.excludeFromMetrics;
  const active = accounts.filter((a) => !deEmph(a));
  const hidden = accounts.filter(deEmph);
  const hiddenOn = hidden.filter((a) => f.accounts.includes(String(a.id))).length;

  const row = (a: Account) => {
    const id = String(a.id);
    const on = f.accounts.includes(id);
    // `archived` wins the badge, as in the old client — an archived account is
    // also usually excluded, and two badges on one row is noise.
    const badge = a.archived ? 'archived' : a.excludeFromMetrics ? 'excluded' : '';
    return (
      <label key={id} className={`filter-opt${on ? ' on' : ''}`}>
        <input type="checkbox" checked={on} onChange={() => f.toggleAccount(id)} />
        <span className="filter-opt-label">{a.name || '(unnamed)'}</span>
        {badge ? <span className="filter-opt-count">{badge}</span> : null}
      </label>
    );
  };

  return (
    <div className="filter-opts">
      <label className={`filter-opt${all ? ' on' : ''}`}>
        <input type="checkbox" checked={all} onChange={() => f.setAccounts([])} />
        <span className="filter-opt-label">All accounts</span>
      </label>

      {active.map(row)}

      <label className={`filter-opt${f.accounts.includes(UNASSIGNED_FILTER_ID) ? ' on' : ''}`}>
        <input
          type="checkbox"
          checked={f.accounts.includes(UNASSIGNED_FILTER_ID)}
          onChange={() => f.toggleAccount(UNASSIGNED_FILTER_ID)}
        />
        {/* Trades whose account is null or points at a deleted account. Without
            this they are visible only under "All accounts" and cannot be
            isolated to reassign. */}
        <span className="filter-opt-label">Unassigned</span>
      </label>

      {/* ⚠ AFTER the real accounts and outside the checkbox list — see the
          header. In a demo it says how to LEAVE, because a reader who opened
          this panel to switch accounts is exactly the reader wondering why
          theirs are missing. */}
      <div className="filter-demo">
        <button
          type="button"
          className="btn"
          data-testid="filter-demo"
          onClick={demo.active ? demo.exit : demo.enter}
        >
          {demo.active ? '← Back to your own accounts' : '🎓 Load the Demo Account'}
        </button>
        <span className="muted filter-demo-note">
          {demo.active
            ? 'You’re looking at sample data. Nothing here is saved.'
            : 'A full sample book — 150 trades, a funded account and its evals. Nothing is saved, and your own data is untouched.'}
        </span>
      </div>

      {hidden.length ? (
        <div className="filter-subsec">
          <button
            type="button"
            className="filter-subsec-h"
            aria-expanded={showHidden}
            onClick={() => setShowHidden((v) => !v)}
          >
            <span aria-hidden="true">{showHidden ? '▾' : '▸'}</span>
            <span className="filter-opt-label">Archived &amp; excluded</span>
            <span className="filter-opt-count">
              {hidden.length}{hiddenOn ? ` · ${hiddenOn} on` : ''}
            </span>
          </button>
          {showHidden ? <div className="filter-subsec-body">{hidden.map(row)}</div> : null}
        </div>
      ) : null}
    </div>
  );
}

function FacetSection(
  { dim, options, active, empty }:
  {
    dim: FacetDim;
    options: {
      value: string | number; label: string; count: number;
      /** Tags only — the swatch the user picked. Absent everywhere else. */
      color?: string | null;
    }[];
    active: readonly (string | number)[];
    empty: string;
  },
) {
  const f = useFilters();
  if (!options.length) return <p className="muted">{empty}</p>;
  return (
    <div className="filter-opts">
      {options.map((o) => {
        const on = active.includes(o.value);
        return (
          <label key={String(o.value)} className={`filter-opt${on ? ' on' : ''}`}>
            <input type="checkbox" checked={on} onChange={() => f.toggleFacet(dim, o.value)} />
            {/* ⚠ The swatch is how a tag is recognised — the user picked the
                colour, and beta shows it here and on the chip. `aria-hidden`
                because the name beside it already carries the meaning. */}
            {o.color
              ? <span className="filter-opt-dot" style={{ background: o.color }} aria-hidden="true" />
              : null}
            <span className="filter-opt-label">{o.label}</span>
            <span className="filter-opt-count">{o.count}</span>
          </label>
        );
      })}
    </div>
  );
}
